import { UPDATE_BUYER_NOTES } from "@lib/graphql/outreach";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { useMutation } from "urql";
import type { InboxItem } from "./types";

interface Props {
  open: boolean;
  item: InboxItem | null;
  onClose: () => void;
  onChanged?: () => void;
}

export function InboxDrawer({ open, item, onClose, onChanged }: Props) {
  const [, updateNotes] = useMutation(UPDATE_BUYER_NOTES);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    setNotes(item?.buyer.notes ?? "");
  }, [item]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !item) return null;

  const handleNotesBlur = async () => {
    if (notes === (item.buyer.notes ?? "")) return;
    await updateNotes({ input: { id: item.buyer.id, notes } });
    onChanged?.();
  };

  const draft = item.draft;
  const timeline = buildTimeline(item);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="inbox-drawer-title"
      className="fixed inset-0 z-40 flex justify-end bg-black/40"
    >
      <button
        type="button"
        aria-label="Close drawer"
        className="absolute inset-0 h-full w-full cursor-default"
        onClick={onClose}
      />
      <aside className="relative z-50 h-full w-full max-w-xl overflow-y-auto bg-card shadow-xl">
        <div className="flex items-start justify-between border-b border-border p-4">
          <div>
            <h2 id="inbox-drawer-title" className="text-lg font-semibold text-text-primary">
              {item.buyer.fullName ?? "Buyer pending import"}
            </h2>
            <p className="mt-0.5 text-xs text-text-muted">
              {item.buyer.title ?? "—"}
              {item.buyer.prospectCompany ? ` · ${item.buyer.prospectCompany.name}` : ""}
            </p>
            {item.buyer.linkedinUrl && (
              <a
                href={item.buyer.linkedinUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-block text-xs text-accent hover:underline"
              >
                Open LinkedIn profile
              </a>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-text-muted hover:bg-card-hover hover:text-text-primary"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {draft && (
          <section className="border-b border-border p-4">
            <h3 className="text-sm font-semibold text-text-primary">Draft</h3>
            {draft.subject && (
              <p className="mt-2 text-sm font-medium text-text-secondary">
                Subject: {draft.subject}
              </p>
            )}
            <pre className="mt-2 whitespace-pre-wrap rounded bg-card-alt p-3 text-sm text-text-primary">
              {draft.body}
            </pre>
          </section>
        )}

        {item.buyer.replyText && (
          <section className="border-b border-border p-4">
            <h3 className="text-sm font-semibold text-text-primary">Logged reply</h3>
            <pre className="mt-2 whitespace-pre-wrap rounded bg-card-alt p-3 text-sm text-text-primary">
              {item.buyer.replyText}
            </pre>
            {item.buyer.replyLoggedAt && (
              <p className="mt-1 text-xs text-text-muted">
                Logged {new Date(item.buyer.replyLoggedAt).toLocaleString()}
              </p>
            )}
          </section>
        )}

        <section className="border-b border-border p-4">
          <h3 className="text-sm font-semibold text-text-primary">Activity</h3>
          <ul className="mt-2 space-y-1 text-xs text-text-muted">
            {timeline.length === 0 ? (
              <li>No recorded activity yet.</li>
            ) : (
              timeline.map((entry) => (
                <li key={entry.key}>
                  <span className="text-text-secondary">{entry.label}</span> — {entry.at}
                </li>
              ))
            )}
          </ul>
        </section>

        <section className="p-4">
          <h3 className="text-sm font-semibold text-text-primary">Notes</h3>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={handleNotesBlur}
            placeholder="Free-form notes on this buyer. Auto-saves."
            rows={5}
            className="mt-2 block w-full rounded-md border border-input-border bg-card-alt px-3 py-2 text-sm text-text-primary"
          />
        </section>
      </aside>
    </div>
  );
}

function buildTimeline(item: InboxItem): Array<{ key: string; label: string; at: string }> {
  const out: Array<{ key: string; label: string; at: string }> = [];
  const fmt = (v: string) => new Date(v).toLocaleString();
  if (item.draft?.createdAt) {
    out.push({ key: "draft_created", label: "Draft created", at: fmt(item.draft.createdAt) });
  }
  if (item.buyer.lastSentDraftAt) {
    out.push({ key: "last_sent", label: "Last draft sent", at: fmt(item.buyer.lastSentDraftAt) });
  }
  if (item.buyer.replyLoggedAt) {
    out.push({ key: "reply_logged", label: "Reply logged", at: fmt(item.buyer.replyLoggedAt) });
  }
  if (item.buyer.snoozedUntil) {
    out.push({ key: "snoozed", label: "Snoozed until", at: fmt(item.buyer.snoozedUntil) });
  }
  return out;
}
